import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import css from './CreativePopup.css';
import { AutoCompleteOptionType } from '../../../components/molecules/autocompleteTypes';
import { CreativeType, CreativeTypes } from '../../types';
import CreativePopupTabs from '../creativePopupTabs/CreativePopupTabs';
import CreativeView from '../creativeView/CreativeView';
import { GeneralPopup, PopupTypes } from '../../../components/partials';
import useCsvDownload from '../../useCsvDownload';
import Icon, { IconTypes } from '../../../components/foundations/Icon';
import SubPageHeader from '../../../components/molecules/SubPageHeader';
import { useAsync } from '../../../utils/customHooks';
import { CreativeExplore } from '../../../services';

const CreativeExploreAPI = new CreativeExplore();

export const CREATIVE_POPUP_TITLE = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.TITLE';
export const DOWNLOAD_TOOLTIP_TITLE = 'STATIC.BUTTONS.DOWNLOAD_CSV';

const CreativePopup = ({
    app,
    source,
    creative,
    selectedMetrics,
    selectedCohorts,
    dateRange,
    displayedCreatives,
    onClose: onCloseProp,
    onSimilarCreativeClick,
    isLoadingMetrics = false,
    isLoadingCohorts = false,
}) => {
    const assetRef = useRef();
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showGoBackConfirmation, setShowGoBackConfirmation] = useState(false);

    const [{ data: creativeClusterEntries, isLoading: isLoadingSimilarCreatives, error: similarCreativesError }, runCreativeClusterFetch] = useAsync();

    useEffect(() => {
        runCreativeClusterFetch(
            CreativeExploreAPI.getCreativeCluster(
                creative.imageHash,
                app.value,
                creative.type.toLowerCase()
            )
        );
    }, [creative.imageHash, creative.type]);

    const similarCreatives = useMemo(() => {
        const scoreByHash = new Map(
            (creativeClusterEntries || []).map(({ creative_hash: hash, similarity_score: score }) => [hash, score])
        );
        return displayedCreatives
            ?.filter(({ imageHash }) => scoreByHash.has(imageHash))
            .map(c => ({ ...c, similarityScore: scoreByHash.get(c.imageHash) }))
            .sort((a, b) => b.similarityScore - a.similarityScore) || [];
    }, [creativeClusterEntries, displayedCreatives]);

    const creativesForDownload = useMemo(() => [creative, ...similarCreatives], [creative, similarCreatives]);

    const { onCsvDownloadClicked, isLoadingCreativesTags } = useCsvDownload({
        creatives: creativesForDownload,
        selectedAppOption: app,
        selectedSourceOption: source,
        dateRange,
        selectedCohorts,
        selectedTab: null,
        insightsDownloadUrl: null,
        isPopupDownload: true,
    });

    const handleClose = useCallback(() => {
        if (creative.type === CreativeTypes.PLAYABLE && assetRef.current) assetRef.current.style.pointerEvents = 'none';
        onCloseProp();
    }, [creative.type, onCloseProp]);

    const onClose = useCallback(() => {
        if (hasUnsavedChanges) {
            setShowGoBackConfirmation(true);
        } else {
            handleClose();
        }
    }, [hasUnsavedChanges, handleClose]);

    const handleConfirmClose = () => {
        setShowGoBackConfirmation(false);
        handleClose();
    };

    const handleCancelClose = () => {
        setShowGoBackConfirmation(false);
    };

    const closeOnEscape = useCallback(event => event.key === 'Escape' && onClose(), [onClose]);

    useEffect(() => {
        document.addEventListener('keydown', closeOnEscape, false);
        return () => document.removeEventListener('keydown', closeOnEscape, false);
    }, [closeOnEscape]);

    useEffect(() => {
        const handleBeforeUnload = event => {
            if (hasUnsavedChanges) {
                event.preventDefault();
                event.returnValue = '';
                alert('You have unsaved changes. Are you sure you want to leave?');
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    return (
        <div className={css.creativePopup}>
            <GeneralPopup
                open={showGoBackConfirmation}
                type={PopupTypes.INFO_WITH_CANCEL}
                title="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.TITLE"
                text="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.MESSAGE"
                acceptText="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.ACCEPT"
                rejectText="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.REJECT"
                onAccept={handleConfirmClose}
                onReject={handleCancelClose}
            />
            <SubPageHeader
                onGoBack={onClose}
                labelTranslationKey={CREATIVE_POPUP_TITLE}
                className={css.header}
                rightSection={
                    <Icon
                        name="file-download"
                        type={IconTypes.TABLER}
                        onClick={onCsvDownloadClicked}
                        disabled={isLoadingCreativesTags || isLoadingSimilarCreatives}
                        hoverText={DOWNLOAD_TOOLTIP_TITLE}
                    />
                }
            />
            <div className={css.content}>
                <div className={css.creativeData}>
                    <CreativeView
                        ref={assetRef}
                        creative={creative}
                        app={app}
                        source={source}
                        similarCreatives={similarCreatives}
                        isLoadingSimilarCreatives={isLoadingSimilarCreatives}
                        similarCreativesError={!!similarCreativesError}
                        onSimilarCreativeClick={onSimilarCreativeClick}
                    />
                </div>
                <CreativePopupTabs
                    app={app.value}
                    source={source.value}
                    creative={creative}
                    selectedMetrics={selectedMetrics}
                    selectedCohorts={selectedCohorts}
                    dateRange={dateRange}
                    onHasUnsavedChanges={setHasUnsavedChanges}
                    hasUnsavedChanges={hasUnsavedChanges}
                    isLoadingMetrics={isLoadingMetrics}
                    isLoadingCohorts={isLoadingCohorts}
                    similarCreatives={similarCreatives}
                    isLoadingSimilarCreatives={isLoadingSimilarCreatives}
                    similarCreativesError={!!similarCreativesError}
                />
            </div>
        </div>
    );
};

CreativePopup.propTypes = {
    creative: PropTypes.shape(CreativeType).isRequired,
    app: AutoCompleteOptionType.isRequired,
    source: AutoCompleteOptionType.isRequired,
    selectedMetrics: PropTypes.arrayOf(AutoCompleteOptionType),
    selectedCohorts: PropTypes.arrayOf(AutoCompleteOptionType),
    dateRange: PropTypes.object.isRequired,
    onClose: PropTypes.func,
    onSimilarCreativeClick: PropTypes.func.isRequired,
    isLoadingMetrics: PropTypes.bool,
    isLoadingCohorts: PropTypes.bool,
    displayedCreatives: PropTypes.arrayOf(CreativeType).isRequired
};

export default CreativePopup;
